import { computed, reactive, ref } from "vue";
import { createAuthApi, createStudioApi, createWorksApi } from "../api/beadApi";
import { AUTH_TOKEN_KEY, getStoredAuthToken, setStoredAuthToken } from "../api/client";
import { createWorkThumbnail } from "../utils/imageThumbnail";
import { useOperationConfig } from "./useOperationConfig";
import { useToast } from "./useToast";
import { useAnalytics } from "./useAnalytics";

export const MODES = [
  {
    key: "simple-pattern",
    title: "简易图纸",
    description: "快速生成网格图纸，游客也可以免费预览。"
  },
  {
    key: "color-recognition",
    title: "色号识别",
    description: "识别品牌色号与主色分布，方便核对和补货。"
  },
  {
    key: "batch-colors",
    title: "颜色批量提取",
    description: "一次获得整张图中的颜色和数量统计。"
  },
  {
    key: "pro-pattern",
    title: "专业图纸",
    description: "适合细节更多的大尺寸图纸和正式交付。"
  }
];

const state = reactive({
  gridWidth: 50,
  gridHeight: 50,
  longEdge: 50,
  preserveAspect: true,
  sourceAspect: 1,
  maxColors: 30,
  removeBackground: true,
  focusSubject: true,
  cartoonStrength: 48,
  paletteKey: "mard",
  status: "请选择一张图片开始创作。",
  currentMode: "",
  totalCount: "-",
  estimatedHours: "-",
  authStatus: "生成登录口令后，关注公众号并发送验证码即可登录。",
  authTag: "未登录"
});

const previewUrl = ref("");
const sourcePreviewUrl = ref("");
const palettes = ref([]);
const stats = ref([]);
const currentFile = ref(null);
const lastPatternResult = ref(null);
const savedWork = ref(null);
const saveWorkBusy = ref(false);
const authToken = ref(getStoredAuthToken());
const currentUser = ref(authToken.value ? createDefaultUser() : null);
const loginChallenge = ref(null);
const emailAuthBusy = ref(false);
const emailAuthStatus = ref("");
const emailCodeSentNonce = ref(0);
const busy = ref(false);
const initialized = ref(false);
const HOME_DRAFT_KEY = "bead_home_editor_draft";

let loginPollTimer = null;

const authApi = computed(() => createAuthApi());
const studioApi = computed(() => createStudioApi());
const worksApi = computed(() => createWorksApi());
const { toast } = useToast();
const { track } = useAnalytics();
const operationConfig = useOperationConfig();
const isLoggedIn = computed(() => Boolean(authToken.value || currentUser.value?.loggedIn));
const publishEnabled = computed(() =>
  operationConfig.isEnabled("community.publish.enabled", true)
);
const hasFile = computed(() => Boolean(currentFile.value));
const currentPaletteLabel = computed(() => {
  const found = palettes.value.find((item) => item.key === state.paletteKey);
  return found ? found.label : "品牌色卡";
});

function createDefaultUser(extra = {}) {
  return {
    loggedIn: true,
    nickName: "拼豆玩家",
    avatar: "",
    ...extra
  };
}

function persistLogin(payload = {}) {
  const nextToken = payload.loginToken || payload.token || payload.accessToken || payload.authToken || "";
  if (nextToken) {
    window.localStorage.setItem('userInfo', JSON.stringify(payload));
    setStoredAuthToken(nextToken);
    authToken.value = nextToken;
  }
  currentUser.value = createDefaultUser({
    loginType: payload.loginType,
    openId: payload.openId,
    nickName: payload.nickName || payload.nickname || "拼豆玩家",
    nickname: payload.nickname || payload.nickName || "拼豆玩家",
    avatar: payload.avatar || ""
  });

  state.authStatus = "登录成功，高清下载、作品保存和专业模式已经解锁。";
  state.authTag = "已登录";
  loginChallenge.value = null;
  clearLoginPolling();
  toast.success("登录成功，欢迎来到拼豆物语。", {
    title: "登录成功"
  });
}

function isSessionError(error) {
  const message = String(error?.message || "");
  return /登录|口令|令牌|token|过期|失效|未认证|未授权/i.test(message);
}

async function sendEmailLoginCode(email) {
  if (emailAuthBusy.value) return false;
  emailAuthBusy.value = true;
  emailAuthStatus.value = "正在发送验证码...";
  try {
    await authApi.value.sendEmailLoginCode(email);
    emailCodeSentNonce.value += 1;
    emailAuthStatus.value = "验证码已发送，请在 5 分钟内完成登录。";
    return true;
  } catch (error) {
    emailAuthStatus.value = error?.message || "验证码发送失败，请稍后重试。";
    return false;
  } finally {
    emailAuthBusy.value = false;
  }
}

async function loginWithEmail(payload = {}) {
  if (emailAuthBusy.value) return false;
  emailAuthBusy.value = true;
  emailAuthStatus.value = "正在验证...";
  try {
    const response = await authApi.value.loginWithEmail(payload.email, payload.code);
    emailAuthStatus.value = "验证成功，正在进入。";
    persistLogin(response);
    return true;
  } catch (error) {
    emailAuthStatus.value = error?.message || "登录失败，请检查验证码。";
    return false;
  } finally {
    emailAuthBusy.value = false;
  }
}

async function initialize() {
  if (initialized.value) return;
  initialized.value = true;
  if (authToken.value) {
    state.authStatus = "已检测到登录信息，正在恢复账号状态。";
    state.authTag = "已登录";
  }
  await Promise.all([
    loadPalettes(),
    loadAuthBootstrap(),
    loadCurrentUser(),
    operationConfig.load()
  ]);
  await loadHomeEditorDraft();
}

async function loadPalettes() {
  try {
    const response = await studioApi.value.fetchPalettes();
    palettes.value = Array.isArray(response)
      ? response
      : Array.isArray(response?.items)
        ? response.items
        : [];
    if (palettes.value.length && !palettes.value.some((item) => item.key === state.paletteKey)) {
      state.paletteKey = palettes.value[0].key;
    }
  } catch (error) {
    state.status = "色卡暂时加载失败，请稍后刷新页面。";
  }
}

async function loadAuthBootstrap() {
  try {
    const payload = await authApi.value.fetchBootstrap(buildRedirectUri());
    if (!isLoggedIn.value) {
      state.authStatus = payload.recommendedFlow || state.authStatus;
      state.authTag = payload.browserContext || "未登录";
    }
  } catch (error) {
    if (!isLoggedIn.value) {
      state.authStatus = "登录服务暂时不可用，游客预览仍可正常使用。";
      state.authTag = "游客";
    }
  }
}

async function loadCurrentUser() {
  try {
    const payload = await authApi.value.fetchCurrentUser();
    if (payload?.loginToken) {
      setStoredAuthToken(payload.loginToken);
      authToken.value = payload.loginToken;
    }
    if (payload?.loggedIn || authToken.value) {
      currentUser.value = createDefaultUser(payload);
      state.authStatus = "当前账号已登录，可以使用全部创作能力。";
      state.authTag = "已登录";
      clearLoginPolling();
    } else if (!authToken.value) {
      currentUser.value = { loggedIn: false };
    }
  } catch (error) {
    if (authToken.value && isSessionError(error)) {
      setStoredAuthToken("");
      authToken.value = "";
      currentUser.value = { loggedIn: false };
      state.authStatus = "登录状态已过期，请重新登录。";
      state.authTag = "未登录";
    } else {
      currentUser.value = authToken.value ? createDefaultUser() : null;
    }
  }
}

async function onFileChange(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  if (sourcePreviewUrl.value.startsWith("blob:")) {
    URL.revokeObjectURL(sourcePreviewUrl.value);
  }
  currentFile.value = file;
  // 用稳定的 data URL 保存上传原图，避免异步发布时 blob URL 已失效。
  sourcePreviewUrl.value = await fileToDataUrl(file);
  state.sourceAspect = await detectSubjectAspect(file);
  applySizePreset(state.longEdge || 40);
  previewUrl.value = "";
  stats.value = [];
  lastPatternResult.value = null;
  savedWork.value = null;
  state.totalCount = "-";
  state.estimatedHours = "-";
  state.status = isLoggedIn.value
    ? "图片已加载，请选择生成方式。"
    : "图片已加载，游客可以免费生成简易预览。";
}

async function importImageDataUrl(dataUrl, sourceLabel) {
  if (!dataUrl?.startsWith("data:image/")) return;
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  currentFile.value = new File([blob], "home-editor-draft.png", { type: blob.type || "image/png" });
  state.sourceAspect = await detectSubjectAspect(currentFile.value);
  applySizePreset(state.longEdge || 40);
  sourcePreviewUrl.value = dataUrl;
  previewUrl.value = "";
  state.status = `${sourceLabel || "首页"}图片已导入，可以开始生成图纸。`;
}

async function loadHomeEditorDraft() {
  try {
    const dataUrl = window.sessionStorage.getItem(HOME_DRAFT_KEY);
    if (!dataUrl) return;
    window.sessionStorage.removeItem(HOME_DRAFT_KEY);
    await importImageDataUrl(dataUrl, "首页");
  } catch (error) {
    state.status = "检测到首页草稿，但导入失败，请重新选择图片。";
  }
}

async function runMode(mode) {
  if (!isLoggedIn.value && mode !== "simple-pattern") {
    state.status = "专业模式需要登录后使用，游客可以先生成简易图纸。";
    state.authStatus = "登录后可使用色号识别、颜色提取和专业图纸模式。";
    window.dispatchEvent(new CustomEvent("bead-open-login"));
    return false;
  }
  if (!currentFile.value) {
    state.status = "请先选择一张图片。";
    toast.info("请先上传一张清晰的图片，再开始生成图纸。", { title: "还没有图片" });
    return false;
  }

  busy.value = true;
  state.currentMode = mode;
  state.status = "正在分析图片并匹配拼豆色号...";

  try {
    const data = await studioApi.value.requestMode(mode, currentFile.value, {
      gridWidth: state.gridWidth,
      gridHeight: state.gridHeight,
      longEdge: state.longEdge,
      preserveAspect: state.preserveAspect,
      maxColors: state.maxColors,
      paletteKey: state.paletteKey,
      smooth: true,
      cleanup: true,
      algorithmVersion: "v5",
      preserveAspectRatio: state.preserveAspect,
      processingStyle: "cartoon",
      removeBackground: state.removeBackground,
      focusSubject: state.focusSubject,
      cartoonStrength: state.cartoonStrength
    });
    const resultImage = String(data?.resultImageBase64 || data?.previewImageBase64 || "").trim();
    const resultPixels = Array.isArray(data?.editedPixels)
      ? data.editedPixels
      : Array.isArray(data?.pixels)
        ? data.pixels
        : [];
    const hasBeads = Number(data?.totalCount || 0) > 0 || resultPixels.some((pixel) => pixel && pixel.empty !== true);
    if (!resultImage || !resultPixels.length || !hasBeads) {
      const emptyError = new Error("没有生成可用图纸。请换一张主体更清晰、留白更少的图片，或调整尺寸后重试。");
      emptyError.code = "EMPTY_PATTERN";
      throw emptyError;
    }
    previewUrl.value = resultImage;
    stats.value = data.stats || [];
    state.totalCount = data.totalCount ?? "-";
    state.estimatedHours = data.estimatedHours ? `${data.estimatedHours} 小时` : "-";
    lastPatternResult.value = {
      ...data,
      mode,
      paletteKey: data.paletteKey || state.paletteKey,
      previewImage: previewUrl.value,
      sourceImageBase64: sourcePreviewUrl.value.startsWith("data:") ? sourcePreviewUrl.value : ""
    };
    savedWork.value = null;
    state.status = isLoggedIn.value
      ? "图纸生成完成，可以下载或保存作品。"
      : "游客预览已生成，登录后可以下载高清图纸和保存作品。";
    return true;
  } catch (error) {
    const message = error?.message === "LOGIN_REQUIRED"
      ? "这个模式需要先登录。"
      : error?.message || "图纸生成失败，请检查网络后重试。";
    if (error?.code === "EMPTY_PATTERN") {
      previewUrl.value = "";
      stats.value = [];
      lastPatternResult.value = null;
      state.totalCount = "-";
      state.estimatedHours = "-";
    }
    state.status = message;
    toast.error(message, { title: error?.code === "EMPTY_PATTERN" ? "没有识别到有效图纸" : "生成失败" });
    return false;
  } finally {
    busy.value = false;
  }
}

function applySizePreset(longEdge) {
  const edge = Math.max(8, Math.min(160, Number(longEdge) || 40));
  state.longEdge = edge;
  if (!state.preserveAspect) {
    state.gridWidth = edge;
    state.gridHeight = edge;
    return;
  }
  const aspect = Math.max(0.1, Math.min(10, state.sourceAspect || 1));
  if (aspect >= 1) {
    state.gridWidth = edge;
    state.gridHeight = Math.max(8, Math.round(edge / aspect));
  } else {
    state.gridWidth = Math.max(8, Math.round(edge * aspect));
    state.gridHeight = edge;
  }
}

function setPreserveAspect(value) {
  state.preserveAspect = Boolean(value);
  applySizePreset(state.longEdge || Math.max(state.gridWidth, state.gridHeight));
}

function setCustomGridWidth(value) {
  const width = Math.max(8, Math.min(160, Number(value) || 8));
  const aspect = Math.max(0.1, Math.min(10, state.sourceAspect || 1));
  state.gridWidth = width;
  state.gridHeight = Math.max(8, Math.min(160, Math.round(width / aspect)));
  state.longEdge = 0;
  state.preserveAspect = true;
}

function setCustomGridHeight(value) {
  const height = Math.max(8, Math.min(160, Number(value) || 8));
  const aspect = Math.max(0.1, Math.min(10, state.sourceAspect || 1));
  state.gridHeight = height;
  state.gridWidth = Math.max(8, Math.min(160, Math.round(height * aspect)));
  state.longEdge = 0;
  state.preserveAspect = true;
}

async function detectSubjectAspect(file) {
  try {
    const bitmap = await createImageBitmap(file);
    const originalAspect = bitmap.width / bitmap.height;
    bitmap.close?.();
    return originalAspect;
  } catch (error) {
    return 1;
  }
}

async function saveCurrentWork(extra = {}) {
  await operationConfig.load();
  if (!publishEnabled.value) {
    state.status = "作品发布暂时关闭，请稍后再试。";
    toast.info(state.status, { title: "发布维护中" });
    return null;
  }
  if (!isLoggedIn.value) {
    state.status = "登录后才能保存作品。";
    window.dispatchEvent(new CustomEvent("bead-open-login"));
    return null;
  }
  if (!previewUrl.value || !lastPatternResult.value) {
    state.status = "请先生成一张图纸。";
    return null;
  }

  saveWorkBusy.value = true;
  const token = getStoredAuthToken();
  try {
    state.status = "正在整理作品图片并保存...";
    const displayImage = extra.displayImage || previewUrl.value;
    const thumbnailImage = await createWorkThumbnail(displayImage);
    const originalSource = extra.originalImage || sourcePreviewUrl.value;
    if (!originalSource) {
      throw new Error("上传原图已经失效，请重新选择图片后再保存作品。");
    }
    const [displayImageUrl, thumbnailImageUrl, originalImageUrl] = await Promise.all([
      worksApi.value.prepareWorkImage(displayImage),
      thumbnailImage === displayImage
        ? Promise.resolve("")
        : worksApi.value.prepareWorkImage(thumbnailImage),
      originalSource
        ? worksApi.value.prepareWorkImage(originalSource)
        : Promise.resolve("")
    ]);
    const payload = {
      title: extra.title || `${currentPaletteLabel.value} 图纸 ${state.gridWidth}x${state.gridHeight}`,
      description: extra.description || "由拼豆物语生成的拼豆图纸。",
      tagId: extra.tagId || "original",
      public: extra.public !== false,
      displayImage: displayImageUrl,
      originalImage: originalImageUrl,
      // Keep previewImage during the API transition for older deployed servers.
      previewImage: displayImageUrl,
      thumbnailImage: thumbnailImageUrl || displayImageUrl,
      totalCount: lastPatternResult.value.totalCount || state.totalCount,
      estimatedHours: lastPatternResult.value.estimatedHours || state.estimatedHours,
      paletteKey: lastPatternResult.value.paletteKey || state.paletteKey,
      gridWidth: lastPatternResult.value.gridWidth || state.gridWidth,
      gridHeight: lastPatternResult.value.gridHeight || state.gridHeight,
      pixels: lastPatternResult.value.editedPixels || lastPatternResult.value.pixels || [],
      stats: lastPatternResult.value.stats || stats.value || [],
      wxLoginToken: token,
      visitType: extra.public === false ? 1 : 2
    };
    savedWork.value = await worksApi.value.saveWork(payload);
    track("SAVE", { workId: savedWork.value?.id || savedWork.value?.commentId, metadata: { public: payload.public } });
    track("PUBLISH", { workId: savedWork.value?.id || savedWork.value?.commentId, metadata: { public: payload.public } });
    state.status = payload.public ? "作品已提交审核，审核通过后会展示在社区。" : "作品已保存为仅自己可见。";
    toast.success(payload.public
      ? "作品已经安全保存，运营审核通过后会出现在作品社区；现在可在个人主页查看。"
      : "作品已保存到个人主页，目前仅你自己可见。", {
      title: payload.public ? "已提交审核" : "保存成功",
      duration: 5200
    });
    return savedWork.value;
  } catch (error) {
    if (isSessionError(error)) {
      setStoredAuthToken("");
      authToken.value = "";
      currentUser.value = { loggedIn: false };
      state.status = "登录状态已过期，请重新登录后保存作品。";
      window.dispatchEvent(new CustomEvent("bead-open-login"));
    } else {
      state.status = error.message || "作品保存失败，请稍后再试。";
    }
    toast.error(state.status, {
      title: "保存失败"
    });
    return null;
  } finally {
    saveWorkBusy.value = false;
  }
}

function updatePatternResult(payload = {}) {
  if (!lastPatternResult.value || !Array.isArray(payload.pixels)) return;
  lastPatternResult.value = {
    ...lastPatternResult.value,
    editedPixels: payload.pixels,
    stats: payload.stats || lastPatternResult.value.stats,
    totalCount: payload.totalCount ?? lastPatternResult.value.totalCount,
    previewImage: payload.previewImage || lastPatternResult.value.previewImage
  };
  stats.value = payload.stats || stats.value;
  state.totalCount = payload.totalCount ?? state.totalCount;
  if (payload.previewImage) {
    previewUrl.value = payload.previewImage;
  }
  savedWork.value = null;
  state.status = "图纸已调整，颜色统计和下载内容已同步更新。";
}

async function createMessageLogin() {
  if (isLoggedIn.value) return;
  try {
    clearLoginPolling();
    const payload = await authApi.value.createMessageLoginChallenge(buildRedirectUri());
    loginChallenge.value = payload;
    state.authStatus = payload.message || "扫码关注公众号后发送页面上的 5 位验证码。";
    state.authTag = "等待验证";
    startLoginPolling();
  } catch (error) {
    state.authStatus = error.message || "生成登录验证码失败。";
    state.authTag = "异常";
  }
}

async function refreshMessageLoginStatus() {
  try {
    if (!loginChallenge.value?.loginToken) {
      state.authStatus = "请先生成一组登录验证码。";
      return;
    }
    const payload = await authApi.value.fetchMessageLoginStatus(loginChallenge.value.loginToken);
    state.authStatus = payload.message || state.authStatus;
    if (payload.status === "AUTHORIZED" || payload.loggedIn || payload.loginToken) {
      persistLogin(payload);
    } else if (payload.status === "EXPIRED") {
      state.authTag = "已过期";
      clearLoginPolling();
    }
  } catch (error) {
    state.authStatus = error.message || "登录状态刷新失败。";
  }
}

function startLoginPolling() {
  clearLoginPolling();
  loginPollTimer = window.setInterval(() => {
    refreshMessageLoginStatus().catch(() => { });
  }, 3000);
}

function clearLoginPolling() {
  if (!loginPollTimer) return;
  window.clearInterval(loginPollTimer);
  loginPollTimer = null;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("图片读取失败"));
    reader.readAsDataURL(file);
  });
}

function stopLoginFlow() {
  clearLoginPolling();
}

async function openFollowGuideLink() {
  try {
    const payload = await authApi.value.fetchFollowGuide();
    state.authStatus = payload.message || "已打开关注指引。";
    if (payload.followUrl) window.open(payload.followUrl, "_blank", "noopener");
  } catch (error) {
    state.authStatus = error.message || "获取关注指引失败。";
  }
}

async function logout() {
  clearLoginPolling();
  loginChallenge.value = null;
  const loginToken = authToken.value || getStoredAuthToken();
  try {
    await authApi.value.logout(loginToken);
  } catch (error) {
    // Client state is cleared even if the server session has expired.
  }
  setStoredAuthToken("");
  authToken.value = "";
  currentUser.value = { loggedIn: false };
  state.authStatus = "已退出登录，游客预览仍可使用。";
  state.authTag = "未登录";
}

function buildRedirectUri() {
  return `${window.location.origin}/app-api/quark/auth/wechat/mp-callback`;
}

export function useStudio() {
  return {
    MODES,
    state,
    palettes,
    stats,
    previewUrl,
    sourcePreviewUrl,
    lastPatternResult,
    savedWork,
    saveWorkBusy,
    currentUser,
    loginChallenge,
    emailAuthBusy,
    emailAuthStatus,
    emailCodeSentNonce,
    busy,
    hasFile,
    isLoggedIn,
    publishEnabled,
    currentPaletteLabel,
    initialize,
    loadCurrentUser,
    onFileChange,
    applySizePreset,
    setPreserveAspect,
    setCustomGridWidth,
    setCustomGridHeight,
    importImageDataUrl,
    runMode,
    saveCurrentWork,
    updatePatternResult,
    createMessageLogin,
    refreshMessageLoginStatus,
    sendEmailLoginCode,
    loginWithEmail,
    stopLoginFlow,
    openFollowGuideLink,
    logout
  };
}
